import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { fetchClients, fetchQuotes, createQuote, updateQuote, deleteQuote } from '../models/QuoteService';
import { createInvoice, fetchInvoices } from '../models/InvoiceService';
import { useAuth } from '../models/AuthContext';
import { toDate, parsePrice, itemLabel } from '../models/urssaf';
import PageShell, { Btn, Pill, fmtEUR } from '../components/design/PageShell';
import ClientFilter from '../components/design/ClientFilter';
import { Ic } from '../components/design/Icons';
import GridProtocolPDF from '../components/GridProtocolPDF';
import { BlobProvider } from '@react-pdf/renderer';

const clientNameOf = (doc) =>
  (typeof doc.client === 'string' ? doc.client : (doc.client?.Title || doc.client?.name)) || '—';

const EMITTER = {
  name: 'Matthieu SIMAR',
  address: '2550 Lignerolles',
  city: '27160 Breteuil-sur-Iton',
  email: 'matthieu.simar@gmail.com',
  siret: '753 456 102 00023',
  siren: '753 456 102',
};

const quoteTotal = (q) => {
  if (!q.products || !Array.isArray(q.products)) return 0;
  return q.products.reduce((acc, p) => acc + (Number(p.quantity) || 0) * parsePrice(p.unitPrice), 0);
};

const fmtDate = (d) => {
  const date = toDate(d);
  if (!date) return '—';
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const slugify = (s) => (s || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-zA-Z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 40);

const buildPdfFileName = (label, prefix, doc, clientName) => {
  const date = toDate(doc.created) || new Date();
  const year = date.getFullYear();
  const numStr = String(doc.num).padStart(4, '0');
  const client = slugify(clientName);
  return `${label}-${prefix}-${year}-${numStr}${client ? '-' + client : ''}.pdf`;
};

const forceDownload = (url, fileName) => {
  if (!url) return;
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); }, 100);
  } catch (e) {
    console.error('Download error', e);
    window.open(url, '_blank');
  }
};

const Quotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState(null);

  const { currentUser } = useAuth();
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const loadAll = useCallback(async () => {
    const results = await Promise.allSettled([fetchQuotes(), fetchClients()]);
    if (!aliveRef.current) return true;
    const [qs, cls] = results;
    if (qs.status === 'fulfilled') setQuotes(qs.value);
    if (cls.status === 'fulfilled') setClients(cls.value);
    results.filter(r => r.status === 'rejected')
      .forEach(r => console.error('[Devis] chargement partiel:', r.reason));
    return qs.status === 'fulfilled';
  }, []);

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    const run = async () => {
      const ok = await loadAll();
      if (cancelled || !aliveRef.current) return;
      if (ok) { setLoadError(null); setLoading(false); return; }
      if (attempt < 2) {
        attempt += 1;
        setTimeout(() => { if (!cancelled) run(); }, 600 * attempt);
        return;
      }
      setLoadError('Impossible de charger les devis.');
      setLoading(false);
    };
    setLoading(true);
    run();
    return () => { cancelled = true; };
  }, [loadAll, currentUser?.uid]);

  const retry = () => {
    setLoading(true);
    setLoadError(null);
    loadAll().then((ok) => {
      if (!aliveRef.current) return;
      if (!ok) setLoadError('Impossible de charger les devis.');
      setLoading(false);
    });
  };

  const stats = useMemo(() => {
    const now = new Date();
    const q = Math.floor(now.getMonth() / 3);
    const qS = new Date(now.getFullYear(), q * 3, 1);
    const qE = new Date(now.getFullYear(), q * 3 + 3, 0, 23, 59, 59);

    const open = quotes.filter(q => !q.converted && !q.rejected);
    const accepted = quotes.filter(q => {
      const d = toDate(q.created);
      return q.converted && d && d >= qS && d <= qE;
    });
    const total = quotes.length;
    const conv = total > 0 ? Math.round((accepted.length / total) * 100) : 0;
    const openTotal = open.reduce((a, q) => a + quoteTotal(q), 0);
    const acceptedTotal = accepted.reduce((a, q) => a + quoteTotal(q), 0);

    return { open: open.length, openTotal, accepted: accepted.length, acceptedTotal, conv };
  }, [quotes]);

  /** Clients ayant au moins un devis, les plus récents en tête. */
  const clientOptions = useMemo(() => {
    const map = new Map();
    quotes.forEach(q => {
      const name = clientNameOf(q);
      const d = toDate(q.created) || new Date(0);
      const cur = map.get(name);
      if (cur) { cur.count += 1; if (d > cur.last) cur.last = d; }
      else map.set(name, { name, count: 1, last: d });
    });
    return [...map.values()].sort((a, b) => b.last - a.last);
  }, [quotes]);

  // Les derniers devis en haut.
  const sorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    return quotes
      .filter(x => {
        if (clientFilter && clientNameOf(x) !== clientFilter) return false;
        if (!q) return true;
        return clientNameOf(x).toLowerCase().includes(q) || String(x.num).includes(q);
      })
      .sort((a, b) => (toDate(b.created) || 0) - (toDate(a.created) || 0));
  }, [quotes, search, clientFilter]);

  const handleDelete = async (q) => {
    if (!window.confirm(`Supprimer le devis #${q.num} ?`)) return;
    try {
      await deleteQuote(q.id);
      setQuotes(quotes.filter(x => x.id !== q.id));
      setSelected(null);
    } catch (e) { console.error(e); }
  };

  const handleConvert = async (q) => {
    if (!window.confirm(`Convertir le devis #${q.num} en facture ?`)) return;
    try {
      const invs = await fetchInvoices();
      const nextNum = (invs.reduce((m, i) => Math.max(m, i.num || 0), 0)) + 1;
      await createInvoice({
        client: q.client, clientId: q.clientId,
        created: new Date(), prestaDate: toDate(q.prestaDate) || new Date(),
        products: q.products, num: nextNum,
      });
      await updateQuote(q.id, { converted: true });
      setQuotes(quotes.map(x => x.id === q.id ? { ...x, converted: true } : x));
      setSelected(null);
    } catch (e) { console.error(e); alert('Erreur lors de la conversion'); }
  };

  return (
    <PageShell
      section="devis"
      number="03"
      title="Devis"
      subtitle="Propositions commerciales · conversion en facture"
      actions={<Btn kind="primary" icon="plus" onClick={() => setFormOpen(true)}>Nouveau devis</Btn>}
    >
      <div className="ta-filters">
        <div className="ta-search">
          <span className="ta-search-ic">{Ic.search}</span>
          <input
            placeholder="Rechercher par client ou numéro…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <ClientFilter
        clients={clientOptions}
        value={clientFilter}
        onChange={setClientFilter}
        total={quotes.length}
      />

      {loadError && (
        <div className="ta-callout ta-callout-warn" style={{ alignItems: 'center' }}>
          <span style={{ flex: 1 }}>{loadError}</span>
          <Btn kind="ghost" className="ta-btn-sm" onClick={retry}>Réessayer</Btn>
        </div>
      )}

      <section className="ta-stats" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div>
          <div className="eyebrow">{"// "}Actifs</div>
          <div className="f">{stats.open}</div>
          <div className="d tnum">{fmtEUR(stats.openTotal)} €</div>
        </div>
        <div>
          <div className="eyebrow">{"// "}Acceptés (T en cours)</div>
          <div className="f" style={{ color: 'var(--ok)' }}>{stats.accepted}</div>
          <div className="d tnum">{fmtEUR(stats.acceptedTotal)} €</div>
        </div>
        <div>
          <div className="eyebrow">{"// "}Taux conv.</div>
          <div className="f">{stats.conv}</div>
          <div className="d tnum">%</div>
        </div>
      </section>

      <section>
        <div className="ta-thead" style={{ gridTemplateColumns: '70px 1fr 120px 140px 160px 40px' }}>
          <div>N°</div>
          <div>Client</div>
          <div>Émis</div>
          <div>Statut</div>
          <div style={{ textAlign: 'right' }}>Montant</div>
          <div></div>
        </div>
        {loading && <div className="mono" style={{ padding: 24, color: 'var(--ink-3)' }}>Chargement…</div>}
        {!loading && sorted.length === 0 && (
          <div className="ta-trow" style={{ gridTemplateColumns: '1fr' }}>
            <div style={{ color: 'var(--ink-3)' }}>
              {clientFilter ? `Aucun devis pour ${clientFilter}` : 'Aucun devis'}
            </div>
          </div>
        )}
        {!loading && sorted.map(q => {
          const clientName = clientNameOf(q);
          const status = q.converted ? 'converti' : q.rejected ? 'refusé' : 'envoyé';
          const tone = q.converted ? 'ok' : q.rejected ? 'muted' : 'accent';
          return (
            <div
              key={q.id}
              className="ta-trow"
              style={{ gridTemplateColumns: '70px 1fr 120px 140px 160px 40px' }}
              onClick={() => setSelected(q)}
            >
              <div className="mono" style={{ color: 'var(--ink-3)' }}>Q-{String(q.num).padStart(3, '0')}</div>
              <div style={{ fontSize: 13.5 }}>{clientName}</div>
              <div className="mono tnum" style={{ color: 'var(--ink-2)', fontSize: 12 }}>{fmtDate(q.created)}</div>
              <div><Pill tone={tone}>{status}</Pill></div>
              <div className="mono tnum" style={{ textAlign: 'right', fontSize: 14, fontWeight: 500 }}>
                {fmtEUR(quoteTotal(q))} €
              </div>
              <div style={{ textAlign: 'right' }}>
                <span className="mono" style={{ color: 'var(--ink-4)' }}>→</span>
              </div>
            </div>
          );
        })}
      </section>

      {selected && (
        <QuoteDrawer
          quote={selected}
          clients={clients}
          onClose={() => setSelected(null)}
          onDelete={handleDelete}
          onConvert={handleConvert}
          onEdit={(q) => { setEditing(q); setSelected(null); }}
          onReject={async () => {
            try {
              await updateQuote(selected.id, { rejected: true });
              setQuotes(quotes.map(x => x.id === selected.id ? { ...x, rejected: true } : x));
              setSelected(null);
            } catch (e) { console.error(e); }
          }}
        />
      )}

      {formOpen && (
        <QuoteForm
          clients={clients}
          nextNum={(quotes.reduce((m, q) => Math.max(m, q.num || 0), 0)) + 1}
          onClose={() => setFormOpen(false)}
          onSave={async (payload) => {
            try {
              const id = await createQuote(payload);
              setQuotes([...quotes, { id, ...payload }]);
              setFormOpen(false);
            } catch (e) { console.error(e); alert('Erreur lors de la création'); }
          }}
        />
      )}

      {editing && (
        <QuoteForm
          clients={clients}
          quote={editing}
          onClose={() => setEditing(null)}
          onSave={async (payload) => {
            try {
              await updateQuote(editing.id, payload);
              const updated = { ...editing, ...payload };
              setQuotes(prev => prev.map(x => (x.id === editing.id ? updated : x)));
              setEditing(null);
              setSelected(updated);
            } catch (e) { console.error(e); alert('Erreur lors de l\'enregistrement'); }
          }}
        />
      )}
    </PageShell>
  );
};

function QuoteDrawer({ quote, clients, onClose, onDelete, onConvert, onReject, onEdit }) {
  const [pdfReady, setPdfReady] = useState(false);
  const clientName = clientNameOf(quote);
  const total = quoteTotal(quote);
  const status = quote.converted ? 'converti' : quote.rejected ? 'refusé' : 'envoyé';
  const tone = quote.converted ? 'ok' : quote.rejected ? 'muted' : 'accent';
  const clientObj = quote.clientData ||
    (typeof quote.client === 'object' && quote.client !== null ? quote.client : null) ||
    (clients || []).find(c => c.id === quote.clientId) ||
    (clients || []).find(c => (c.Title || c.name) === clientName) ||
    { Title: clientName, name: clientName, address: '', city: '', email: '' };

  return (
    <div className="ta-drawer-scrim" onClick={onClose}>
      <div className="ta-drawer-panel" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32 }}>
          <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.2em' }}>
            DEVIS Q-{String(quote.num).padStart(3, '0')}
          </div>
          <button onClick={onClose} className="ta-icon-btn">{Ic.close}</button>
        </div>

        <div style={{ fontSize: 32, letterSpacing: '-0.02em', marginBottom: 8 }}>{clientName}</div>
        <div className="mono" style={{ color: 'var(--ink-3)', fontSize: 12, letterSpacing: '0.08em', marginBottom: 32 }}>
          Émis le {fmtDate(quote.created)}{quote.prestaDate ? ` · prestation ${fmtDate(quote.prestaDate)}` : ''}
        </div>

        <div style={{ border: '1px solid var(--rule)', padding: 24, marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Pill tone={tone}>{status}</Pill>
            <div className="mono tnum" style={{ fontSize: 36, color: 'var(--accent)' }}>{fmtEUR(total)} €</div>
          </div>
        </div>

        <div className="eyebrow" style={{ marginBottom: 16 }}>§ Prestations</div>
        <div style={{ borderTop: '1px solid var(--rule)' }}>
          {(quote.products || []).map((p, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: '40px 1fr 60px 100px 120px',
              gap: 16, padding: '16px 0', borderBottom: '1px solid var(--rule)', alignItems: 'start',
            }}>
              <div className="mono" style={{ color: 'var(--ink-4)' }}>{String(i + 1).padStart(2, '0')}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{itemLabel(p) || <span style={{ color: 'var(--ink-4)' }}>— sans libellé —</span>}</div>
                {p.description && <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>{p.description}</div>}
                {p.isMerchandise && (
                  <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-4)', marginTop: 5, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                    marchandise
                  </div>
                )}
              </div>
              <div className="mono tnum" style={{ textAlign: 'right' }}>{p.quantity}</div>
              <div className="mono tnum" style={{ textAlign: 'right' }}>{fmtEUR(parsePrice(p.unitPrice))}</div>
              <div className="mono tnum" style={{ textAlign: 'right', color: 'var(--accent)' }}>
                {fmtEUR((Number(p.quantity) || 0) * parsePrice(p.unitPrice))} €
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 48, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {!pdfReady && (
            <Btn kind="accent" icon="download" onClick={() => setPdfReady(true)}>Préparer le PDF</Btn>
          )}
          {pdfReady && (
            <BlobProvider document={<GridProtocolPDF invoice={quote} client={clientObj} emitter={EMITTER} type="quote" />}>
              {({ url, loading, error }) => {
                const fileName = buildPdfFileName('Devis', 'Q', quote, clientName);
                return (
                  <a
                    href={url || '#'}
                    download={fileName}
                    className="ta-btn ta-btn-accent"
                    style={{ opacity: loading || error ? 0.5 : 1, pointerEvents: loading || error ? 'none' : 'auto' }}
                    onClick={(e) => {
                      if (loading || error) { e.preventDefault(); return; }
                      e.preventDefault();
                      forceDownload(url, fileName);
                    }}
                  >
                    <span className="ta-btn-ic">{Ic.download}</span>
                    <span>{error ? 'Erreur PDF' : loading ? 'Génération…' : 'Télécharger PDF'}</span>
                  </a>
                );
              }}
            </BlobProvider>
          )}
          {!quote.converted && !quote.rejected && (
            <Btn kind="primary" onClick={() => onConvert(quote)}>Convertir en facture</Btn>
          )}
          {!quote.converted && !quote.rejected && (
            <Btn kind="ghost" onClick={onReject}>Marquer refusé</Btn>
          )}
          <Btn kind="ghost" icon="doc" onClick={() => onEdit(quote)}>Modifier</Btn>
          <Btn kind="danger" onClick={() => { onDelete(quote); }}>Supprimer</Btn>
        </div>
      </div>
    </div>
  );
}

/** Lignes d'un devis existant remises au format du formulaire. */
const toFormLines = (doc) => {
  const lines = (doc?.products || []).map(p => ({
    title: itemLabel(p),
    description: p.description || '',
    quantity: Number(p.quantity) || 0,
    unitPrice: parsePrice(p.unitPrice),
    isMerchandise: !!p.isMerchandise,
  }));
  return lines.length ? lines : [{ title: '', description: '', quantity: 1, unitPrice: 0, isMerchandise: false }];
};

const dateInputValue = (v, fallbackToToday = false) => {
  const d = toDate(v) || (fallbackToToday ? new Date() : null);
  if (!d) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

function QuoteForm({ clients, nextNum, quote, onClose, onSave }) {
  const isEdit = !!quote;
  const [clientId, setClientId] = useState(quote?.clientId || '');
  const [clientName, setClientName] = useState(quote ? clientNameOf(quote) : '');
  const [num, setNum] = useState(isEdit ? (quote.num ?? nextNum) : nextNum);
  const [prestaDate, setPrestaDate] = useState(
    isEdit ? dateInputValue(quote.prestaDate, true) : dateInputValue(new Date())
  );
  const [products, setProducts] = useState(() => toFormLines(quote));
  const [saving, setSaving] = useState(false);
  const total = products.reduce((a, p) => a + (Number(p.quantity) || 0) * parsePrice(p.unitPrice), 0);

  const updateP = (i, field, value) => {
    setProducts(prev => prev.map((p, j) => (j === i ? { ...p, [field]: value } : p)));
  };

  const submit = async () => {
    if (!clientId && !clientName) { alert('Sélectionner un client'); return; }
    const lines = products
      .filter(p => p.title && p.title.trim())
      .map(p => ({
        title: p.title.trim(),
        description: p.description || '',
        quantity: Number(p.quantity) || 0,
        unitPrice: parsePrice(p.unitPrice),
        isMerchandise: !!p.isMerchandise,
      }));
    if (lines.length === 0) { alert('Ajouter au moins une ligne avec un libellé'); return; }

    const payload = {
      client: clientName,
      clientId,
      prestaDate: new Date(`${prestaDate}T12:00:00`),
      num: Number(num) || 0,
      products: lines,
    };
    if (!isEdit) payload.created = new Date();

    setSaving(true);
    try {
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ta-drawer-scrim" onClick={onClose}>
      <div className="ta-drawer-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32 }}>
          <div>
            <div className="eyebrow" style={{ color: 'var(--accent)' }}>
              § 03.A · {isEdit ? 'Modifier le devis' : 'Nouveau devis'}
            </div>
            <div style={{ fontSize: 32, letterSpacing: '-0.02em', marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span>Q-</span>
              <input
                type="number"
                value={num}
                onChange={(e) => setNum(e.target.value)}
                aria-label="Numéro de devis"
                style={{
                  width: 100, fontSize: 32, letterSpacing: '-0.02em',
                  background: 'transparent', color: 'var(--ink)',
                  border: 0, borderBottom: '1px dashed var(--rule-2)',
                  fontFamily: 'var(--font-mono)', outline: 'none', padding: 0,
                }}
              />
            </div>
            {isEdit && (
              <div className="mono" style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 10, letterSpacing: '0.08em' }}>
                émis le {fmtDate(quote.created)}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="ta-icon-btn" aria-label="Fermer">{Ic.close}</button>
        </div>

        <div className="eyebrow" style={{ marginBottom: 16 }}>§ 01 · Client</div>
        <select
          value={clientId}
          onChange={(e) => {
            const c = clients.find(x => x.id === e.target.value);
            setClientId(e.target.value);
            setClientName(c ? (c.Title || c.name || '') : '');
          }}
          style={{ width: '100%', padding: '14px 12px', background: 'var(--bg-1)', color: 'var(--ink)', border: '1px solid var(--rule-2)', fontSize: 14, marginBottom: 32 }}
        >
          <option value="">— Choisir un client —</option>
          {isEdit && clientName && !clients.some(c => c.id === clientId) && (
            <option value={clientId}>{clientName}</option>
          )}
          {clients.map(c => <option key={c.id} value={c.id}>{c.Title || c.name}</option>)}
        </select>

        <div className="eyebrow" style={{ marginBottom: 16 }}>§ 02 · Date de prestation</div>
        <input
          type="date"
          value={prestaDate}
          onChange={(e) => setPrestaDate(e.target.value)}
          style={{ padding: 12, background: 'var(--bg-1)', color: 'var(--ink)', border: '1px solid var(--rule-2)', fontFamily: 'var(--font-mono)', marginBottom: 32 }}
        />

        <div className="eyebrow" style={{ marginBottom: 16 }}>§ 03 · Prestations</div>
        <div style={{ borderTop: '1px solid var(--rule-strong)', marginBottom: 20 }}>
          {products.map((p, i) => {
            const rowTotal = (Number(p.quantity) || 0) * parsePrice(p.unitPrice);
            return (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '36px 1fr 84px 56px 96px 100px 28px', gap: 12, padding: '14px 0', borderBottom: '1px solid var(--rule)', alignItems: 'center' }}>
                <div className="mono" style={{ color: 'var(--ink-4)' }}>{String(i + 1).padStart(2, '0')}</div>
                <input placeholder="Libellé de la prestation" value={p.title} onChange={(e) => updateP(i, 'title', e.target.value)}
                  style={{ background: 'transparent', border: 0, borderBottom: '1px dashed var(--rule-2)', color: 'var(--ink)', padding: '8px 0', outline: 'none', fontSize: 13 }} />
                <button
                  type="button"
                  onClick={() => updateP(i, 'isMerchandise', !p.isMerchandise)}
                  title="Bascule prestation ↔ marchandise (taux URSSAF différent)"
                  className="mono"
                  style={{
                    fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase',
                    padding: '5px 0', borderRadius: 3, cursor: 'pointer', background: 'transparent',
                    border: `1px solid ${p.isMerchandise ? 'color-mix(in oklch, var(--warn), transparent 50%)' : 'var(--rule-2)'}`,
                    color: p.isMerchandise ? 'var(--warn)' : 'var(--ink-3)',
                  }}
                >
                  {p.isMerchandise ? 'marchand.' : 'presta'}
                </button>
                <input type="number" value={p.quantity} onChange={(e) => updateP(i, 'quantity', e.target.value)}
                  style={{ background: 'transparent', border: 0, borderBottom: '1px dashed var(--rule-2)', color: 'var(--ink)', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13, outline: 'none' }} />
                <input type="number" step="0.01" value={p.unitPrice} onChange={(e) => updateP(i, 'unitPrice', e.target.value)}
                  style={{ background: 'transparent', border: 0, borderBottom: '1px dashed var(--rule-2)', color: 'var(--ink)', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13, outline: 'none' }} />
                <div className="mono tnum" style={{ textAlign: 'right', color: 'var(--accent)' }}>{fmtEUR(rowTotal)} €</div>
                <button type="button" onClick={() => setProducts(products.filter((_, j) => j !== i))} style={{ color: 'var(--ink-4)', background: 'transparent', border: 0, cursor: 'pointer' }} aria-label="Supprimer la ligne">×</button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => setProducts([...products, { title: '', description: '', quantity: 1, unitPrice: 0, isMerchandise: false }])}
            style={{ display: 'block', width: '100%', padding: '14px 0', color: 'var(--ink-4)', background: 'transparent', border: 0, fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.08em', cursor: 'pointer' }}
          >
            ＋ ajouter une ligne
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 32 }}>
          <div style={{ width: 360, paddingTop: 18, borderTop: '2px solid var(--ink)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span className="mono" style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase' }}>Total estimé</span>
            <span className="mono tnum" style={{ fontSize: 28, fontWeight: 500, color: 'var(--accent)' }}>{fmtEUR(total)} €</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Btn kind="ghost" onClick={onClose}>Annuler</Btn>
          <Btn kind="primary" icon={isEdit ? 'check' : undefined} onClick={submit} disabled={saving}>
            {saving ? 'Enregistrement…' : isEdit ? 'Enregistrer les modifications' : 'Créer le devis'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

export default Quotes;
